import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from '../api/axios';
import { 
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, 
    LineChart, Line, AreaChart, Area 
} from 'recharts';
import { 
    FaArrowLeft, FaCalendarAlt, FaTrophy, FaExclamationCircle, 
    FaCheckCircle, FaClock, FaUsers, FaClipboardList, FaChartLine 
} from 'react-icons/fa';

const ClassReport = () => {
    const { classId } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Date default: Ultimi 30 giorni
    const [dates, setDates] = useState(() => {
        const end = new Date();
        const start = new Date();
        start.setMonth(start.getMonth() - 1);
        return {
            from: start.toISOString().split('T')[0],
            to: end.toISOString().split('T')[0]
        };
    });

    // Struttura dati iniziale sicura (evita crash se l'API fallisce)
    const [data, setData] = useState({
        kpi: { active_users: 0, total_attempts: 0, avg_score: 0 },
        best_performers: [],
        trend: [],
        top_tests: [],
        flop_tests: [],
        easiest_tests: [],
        hardest_tests: [],
        score_distribution: []
    });

    const fetchReport = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await axios.get(`/api_class_report.php`, {
                params: {
                    class_id: classId,
                    date_from: dates.from,
                    date_to: dates.to
                }
            });

            if (res.data.success) {
                // Merge dei dati ricevuti con la struttura sicura
                setData(prev => ({ ...prev, ...res.data.data }));
            } else {
                setError(res.data.message || "Errore nel caricamento dei dati.");
            }
        } catch (e) {
            console.error(e);
            setError("Errore di connessione al server.");
        } finally {
            setLoading(false);
        }
    }, [classId, dates]);

    useEffect(() => {
        fetchReport();
    }, [fetchReport]);

    // --- RENDER HELPERS ---

    if (loading) return (
        <div className="d-flex justify-content-center align-items-center vh-100 bg-light">
            <div className="spinner-border text-primary me-3" role="status"></div>
            <span className="fw-bold text-muted">Elaborazione Reportistica in corso...</span>
        </div>
    );

    if (error) return (
        <div className="container p-5 text-center">
            <div className="alert alert-danger shadow-sm">
                <h4 className="alert-heading"><FaExclamationCircle className="me-2"/>Errore</h4>
                <p>{error}</p>
                <button className="btn btn-outline-danger btn-sm" onClick={() => navigate(-1)}>Torna indietro</button>
            </div>
        </div>
    );

    return (
        <div className="container-fluid fade-in p-4 bg-light" style={{minHeight:'100vh'}}>
            {/* HEADER */}
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 gap-3">
                <div className="d-flex align-items-center gap-3">
                    <button className="btn btn-white shadow-sm border rounded-circle p-2" onClick={() => navigate(-1)} title="Indietro">
                        <FaArrowLeft className="text-secondary"/>
                    </button>
                    <div>
                        <h4 className="fw-bold text-primary m-0"><FaChartLine className="me-2"/>Analisi Classe</h4>
                        <small className="text-muted">Report dettagliato performance e attività</small>
                    </div>
                </div>

                {/* FILTRI DATA */}
                <div className="card border-0 shadow-sm">
                    <div className="card-body p-2 d-flex align-items-center gap-2">
                        <FaCalendarAlt className="text-primary ms-2"/>
                        <input 
                            type="date" 
                            className="form-control form-control-sm border-0 bg-light fw-bold text-muted" 
                            value={dates.from} 
                            onChange={e => setDates(d => ({...d, from: e.target.value}))}
                        />
                        <span className="text-muted small">a</span>
                        <input 
                            type="date" 
                            className="form-control form-control-sm border-0 bg-light fw-bold text-muted" 
                            value={dates.to} 
                            onChange={e => setDates(d => ({...d, to: e.target.value}))}
                        />
                    </div>
                </div>
            </div>

            {/* 1. KPI CARDS */}
            <div className="row g-3 mb-4">
                <div className="col-md-4">
                    <div className="card border-0 shadow-sm h-100 overflow-hidden">
                        <div className="card-body d-flex justify-content-between align-items-center">
                            <div>
                                <h6 className="text-muted text-uppercase small fw-bold mb-1">Utenti Attivi</h6>
                                <h2 className="mb-0 fw-bold text-dark">{data.kpi?.active_users || 0}</h2>
                            </div>
                            <div className="bg-primary bg-opacity-10 p-3 rounded-circle text-primary">
                                <FaUsers size={24}/>
                            </div>
                        </div>
                        <div className="progress" style={{height: '4px'}}>
                            <div className="progress-bar bg-primary" style={{width: '70%'}}></div>
                        </div>
                    </div>
                </div>
                <div className="col-md-4">
                    <div className="card border-0 shadow-sm h-100 overflow-hidden">
                        <div className="card-body d-flex justify-content-between align-items-center">
                            <div>
                                <h6 className="text-muted text-uppercase small fw-bold mb-1">Test Svolti</h6>
                                <h2 className="mb-0 fw-bold text-dark">{data.kpi?.total_attempts || 0}</h2>
                            </div>
                            <div className="bg-success bg-opacity-10 p-3 rounded-circle text-success">
                                <FaClipboardList size={24}/>
                            </div>
                        </div>
                        <div className="progress" style={{height: '4px'}}>
                            <div className="progress-bar bg-success" style={{width: '100%'}}></div>
                        </div>
                    </div>
                </div>
                <div className="col-md-4">
                    <div className="card border-0 shadow-sm h-100 overflow-hidden">
                        <div className="card-body d-flex justify-content-between align-items-center">
                            <div>
                                <h6 className="text-muted text-uppercase small fw-bold mb-1">Media Voti</h6>
                                <h2 className="mb-0 fw-bold text-dark">
                                    {parseFloat(data.kpi?.avg_score || 0).toFixed(1)} <small className="fs-6 text-muted">/100</small>
                                </h2>
                            </div>
                            <div className="bg-warning bg-opacity-10 p-3 rounded-circle text-warning">
                                <FaTrophy size={24}/>
                            </div>
                        </div>
                        <div className="progress" style={{height: '4px'}}>
                            <div 
                                className={`progress-bar ${data.kpi?.avg_score < 60 ? 'bg-danger' : 'bg-warning'}`} 
                                style={{width: `${data.kpi?.avg_score || 0}%`}}
                            ></div>
                        </div>
                    </div>
                </div>
            </div>

            {/* 2. MAIN CHARTS AREA */}
            <div className="row g-4 mb-4">
                {/* Trend Temporale */}
                <div className="col-lg-8">
                    <div className="card border-0 shadow-sm h-100">
                        <div className="card-header bg-white py-3 border-0">
                            <h6 className="fw-bold m-0 text-primary">📈 Andamento Risultati</h6>
                        </div>
                        <div className="card-body" style={{height: '350px'}}>
                            {data.trend && data.trend.length > 0 ? (
                                <ResponsiveContainer width="100%" height="100%">
                                    <AreaChart data={data.trend} margin={{top:10, right:30, left:0, bottom:0}}>
                                        <defs>
                                            <linearGradient id="colorScore" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#8884d8" stopOpacity={0.8}/>
                                                <stop offset="95%" stopColor="#8884d8" stopOpacity={0}/>
                                            </linearGradient>
                                        </defs>
                                        <XAxis dataKey="date" tick={{fontSize: 12}} />
                                        <YAxis domain={[0, 100]} />
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                        <Tooltip 
                                            contentStyle={{backgroundColor:'#fff', borderRadius:'8px', border:'none', boxShadow:'0 4px 12px rgba(0,0,0,0.1)'}}
                                        />
                                        <Area type="monotone" dataKey="avg_score" stroke="#8884d8" fillOpacity={1} fill="url(#colorScore)" name="Media Voto" />
                                    </AreaChart>
                                </ResponsiveContainer>
                            ) : (
                                <div className="h-100 d-flex align-items-center justify-content-center text-muted">
                                    Nessun dato temporale disponibile.
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Best Performers */}
                <div className="col-lg-4">
                    <div className="card border-0 shadow-sm h-100">
                        <div className="card-header bg-white py-3 border-0 d-flex justify-content-between align-items-center">
                            <h6 className="fw-bold m-0 text-success">🏆 Top 5 Studenti</h6>
                        </div>
                        <div className="list-group list-group-flush overflow-auto" style={{maxHeight:'350px'}}>
                            {data.best_performers && data.best_performers.length > 0 ? (
                                data.best_performers.map((u, i) => (
                                    <div key={i} className="list-group-item border-0 d-flex align-items-center px-3 py-3 hover-bg">
                                        <div className={`me-3 rounded-circle d-flex align-items-center justify-content-center fw-bold shadow-sm ${i===0 ? 'bg-warning text-white' : 'bg-light text-secondary'}`} style={{width:'35px', height:'35px'}}>
                                            {i+1}
                                        </div>
                                        <div className="flex-grow-1">
                                            <div className="fw-bold text-dark">{u.name}</div>
                                            <div className="small text-muted">{u.tests_count} test completati</div>
                                        </div>
                                        <div className="text-end">
                                            <span className="badge bg-success rounded-pill px-3 py-2">
                                                {parseFloat(u.avg_score).toFixed(1)}
                                            </span>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="p-4 text-center text-muted">Nessun dato disponibile.</div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* 3. TEST STATISTICS */}
            <div className="row g-4 mb-4">
                {/* Distribuzione Voti */}
                <div className="col-md-12">
                    <div className="card border-0 shadow-sm">
                        <div className="card-header bg-white py-3 border-0">
                            <h6 className="fw-bold m-0 text-dark">📊 Distribuzione Voti</h6>
                        </div>
                        <div className="card-body" style={{height: '250px'}}>
                            {data.score_distribution && data.score_distribution.length > 0 ? (
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={data.score_distribution} layout="vertical" margin={{top:5, right:30, left:40, bottom:5}}>
                                        <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                                        <XAxis type="number" hide />
                                        <YAxis dataKey="range_label" type="category" width={120} tick={{fontSize:12}} />
                                        <Tooltip cursor={{fill: 'transparent'}} />
                                        <Bar dataKey="count" fill="#82ca9d" radius={[0, 10, 10, 0]} barSize={30} label={{ position: 'right', fill: '#666' }} />
                                    </BarChart>
                                </ResponsiveContainer>
                            ) : (
                                <div className="text-center text-muted pt-5">Nessun dato di distribuzione.</div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Easiest vs Hardest */}
                <div className="col-md-6">
                    <div className="card border-0 shadow-sm h-100">
                        <div className="card-header bg-white py-3 border-0">
                            <h6 className="fw-bold m-0 text-info"><FaClock className="me-2"/>Test più Veloci (Facili)</h6>
                        </div>
                        <div className="card-body p-0">
                            <ul className="list-group list-group-flush">
                                {data.easiest_tests && data.easiest_tests.map((t, i) => (
                                    <li key={i} className="list-group-item border-0 d-flex justify-content-between align-items-center px-4 py-3">
                                        <span className="text-truncate" style={{maxWidth:'70%'}}>{t.name}</span>
                                        <span className="badge bg-light text-dark border">
                                            {parseFloat(t.avg_minutes).toFixed(1)} min
                                        </span>
                                    </li>
                                ))}
                                {(!data.easiest_tests || data.easiest_tests.length === 0) && <li className="list-group-item text-center text-muted">Nessun dato</li>}
                            </ul>
                        </div>
                    </div>
                </div>

                <div className="col-md-6">
                    <div className="card border-0 shadow-sm h-100">
                        <div className="card-header bg-white py-3 border-0">
                            <h6 className="fw-bold m-0 text-danger"><FaExclamationCircle className="me-2"/>Test con più Bocciature</h6>
                        </div>
                        <div className="card-body p-0">
                            <ul className="list-group list-group-flush">
                                {data.hardest_tests && data.hardest_tests.map((t, i) => (
                                    <li key={i} className="list-group-item border-0 d-flex justify-content-between align-items-center px-4 py-3">
                                        <span className="text-truncate" style={{maxWidth:'70%'}}>{t.name}</span>
                                        <div className="d-flex align-items-center gap-2">
                                            <div className="progress" style={{width:'60px', height:'6px'}}>
                                                <div className="progress-bar bg-danger" style={{width: `${t.fail_rate}%`}}></div>
                                            </div>
                                            <span className="small fw-bold text-danger">
                                                {parseFloat(t.fail_rate).toFixed(0)}%
                                            </span>
                                        </div>
                                    </li>
                                ))}
                                {(!data.hardest_tests || data.hardest_tests.length === 0) && <li className="list-group-item text-center text-muted">Nessun dato</li>}
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ClassReport;